import {
  type CreateOrganization,
  hasElevatedRights,
  type OrganizationType
} from '@stratumn/studio-shared';
import { Loader2, Plus } from 'lucide-react';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  SettingHeader,
  SettingsContainer,
  SettingsContent
} from '@/components/ui/settings';
import useStores from '@/hooks/useStore';
import { useUser } from '@/layout';
import { CreateOrganizationDialog } from '@/pages/Admin/Organizations/CreateOrganizationDialog';
import { OrganizationsAdminTable } from '@/pages/Admin/Organizations/OrganizationsAdminTable';
import { extractErrorMessage } from '@/services/errors/errors';

export const OrganizationsAdminPage = observer(() => {
  const { user, loadingUser } = useUser();
  const { organizationAdminStore } = useStores();
  const [createOpen, setCreateOpen] = useState(false);

  const isPrivileged = hasElevatedRights(user);

  useEffect(() => {
    if (isPrivileged) {
      organizationAdminStore.fetchOrganizations().catch((error: unknown) => {
        toast.error(
          `Failed to load organizations: ${extractErrorMessage(error)}`
        );
      });
    }
  }, [isPrivileged, organizationAdminStore]);

  if (loadingUser) {
    return null;
  }

  if (!isPrivileged) {
    return <Navigate replace to="/" />;
  }

  const { organizations } = organizationAdminStore;
  const mains = organizations.filter(org => !org.primaryOrganization);

  const handleChangePrimary = (
    rowId: string,
    primaryRowId: string | null
  ): void => {
    organizationAdminStore
      .updateLink(rowId, { primaryOrganizationRowId: primaryRowId })
      .then(() => {
        toast.success('Organization link updated');
      })
      .catch((error: unknown) => {
        toast.error(`Failed to update link: ${extractErrorMessage(error)}`);
      });
  };

  const handleToggleExternal = (rowId: string, value: boolean): void => {
    organizationAdminStore
      .updateLink(rowId, { acceptsExternalMembers: value })
      .then(() => {
        toast.success('Organization updated');
      })
      .catch((error: unknown) => {
        toast.error(`Failed to update: ${extractErrorMessage(error)}`);
      });
  };

  const handleChangeType = (rowId: string, type: OrganizationType): void => {
    organizationAdminStore
      .updateLink(rowId, { organizationType: type })
      .then(() => {
        toast.success('Organization type updated');
      })
      .catch((error: unknown) => {
        toast.error(`Failed to update type: ${extractErrorMessage(error)}`);
      });
  };

  const handleCreate = async (dto: CreateOrganization): Promise<boolean> => {
    try {
      await organizationAdminStore.createOrganization(dto);
      toast.success('Organization created');
      setCreateOpen(false);
      return true;
    } catch (error: unknown) {
      toast.error(
        `Failed to create organization: ${extractErrorMessage(error)}`
      );
      return false;
    }
  };

  const renderContent = () => {
    if (organizationAdminStore.loading && organizations.length === 0) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (organizations.length === 0) {
      return (
        <p className="py-8 text-center text-muted-foreground text-sm">
          No organizations found.
        </p>
      );
    }
    return (
      <OrganizationsAdminTable
        disabled={organizationAdminStore.loading}
        mains={mains}
        onChangePrimary={handleChangePrimary}
        onChangeType={handleChangeType}
        onToggleExternal={handleToggleExternal}
        organizations={organizations}
      />
    );
  };

  return (
    <SettingsContainer>
      <SettingHeader
        description="Manage organizations"
        title="Admin Dashboard"
      />
      <SettingsContent className="gap-6 self-start pb-8">
        <div className="rounded-xl bg-card p-6 shadow-verail">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <h3 className="mb-1 font-medium text-lg">Organizations</h3>
              <p className="text-muted-foreground text-sm">
                Link organizations to a primary organization and control whether
                they accept external members.
              </p>
            </div>
            <Button onClick={() => setCreateOpen(true)} size="sm">
              <Plus className="mr-2 size-4" />
              Create organization
            </Button>
          </div>
          {renderContent()}
        </div>
      </SettingsContent>
      <CreateOrganizationDialog
        onCreate={handleCreate}
        onOpenChange={setCreateOpen}
        open={createOpen}
      />
    </SettingsContainer>
  );
});
